import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Layout } from '@/components/Layout';
import { StreakBadge } from '@/components/HeroReward';
import { useStorage } from '@/hooks/useStorage';
import { WORD_BANK, CATEGORIES } from '@/data/wordbank';
import { Progress as ProgressBar } from '@/components/ui/progress';

interface Achievement {
  id: string;
  emoji: string;
  title: string;
  description: string;
  unlocked: boolean;
}

export default function ProgressPage() {
  const { storage, getStats } = useStorage();
  const stats = getStats();

  const categoryProgress = useMemo(() => {
    const progressEntries = Object.values(storage.wordProgress);
    const allWords = [...WORD_BANK, ...storage.customWords];
    
    return CATEGORIES.map(cat => {
      const categoryWords = progressEntries.filter(p => {
        const word = allWords.find(w => w.id === p.wordId);
        return word?.category === cat.id;
      });
      
      const total = categoryWords.length;
      const masteredZh = categoryWords.filter(p => p.masteryZh === 2).length;
      const masteredJp = categoryWords.filter(p => p.masteryJp === 2).length;
      const masteredEn = categoryWords.filter(p => p.masteryEn === 2).length;
      
      return { category: cat, total, masteredZh, masteredJp, masteredEn };
    }).filter(cp => cp.total > 0);
  }, [storage.wordProgress, storage.customWords]);

  const activityCalendar = useMemo(() => {
    const days: { date: string; level: number }[] = [];
    const today = new Date();
    const lastStudyDate = storage.lastStudyDate;
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const level = lastStudyDate && dateStr <= lastStudyDate ? 3 : 0;
      days.push({ date: dateStr, level });
    }
    return days;
  }, [storage.lastStudyDate]);

  const achievements: Achievement[] = useMemo(() => {
    const totalLearned = stats.totalLearned;
    const streak = stats.streak;
    const masteryZh = stats.masteryZh;
    const masteryJp = stats.masteryJp;
    const masteryEn = stats.masteryEn;
    
    return [
      { id: 'streak_3', emoji: '🔥', title: '连续3天', description: '连续学习3天', unlocked: streak >= 3 },
      { id: 'streak_7', emoji: '💪', title: '连续7天', description: '连续学习7天', unlocked: streak >= 7 },
      { id: 'streak_30', emoji: '👑', title: '连续30天', description: '连续学习30天', unlocked: streak >= 30 },
      { id: 'words_10', emoji: '📚', title: '学10词', description: '学习了10个单词', unlocked: totalLearned >= 10 },
      { id: 'words_50', emoji: '🌟', title: '学50词', description: '学习了50个单词', unlocked: totalLearned >= 50 },
      { id: 'words_100', emoji: '🏆', title: '学100词', description: '学习了100个单词', unlocked: totalLearned >= 100 },
      { id: 'pinyin_master', emoji: '🎯', title: '拼音达人', description: '中文掌握度达80%', unlocked: masteryZh >= 80 },
      { id: 'trilingual', emoji: '🌈', title: '三语全开', description: '三语掌握度均达50%', unlocked: masteryZh >= 50 && masteryJp >= 50 && masteryEn >= 50 },
    ];
  }, [stats]);

  const calColor = (level: number) =>
    level === 0 ? 'bg-muted/30 border border-border' :
    level === 1 ? 'bg-activity-low' :
    level === 2 ? 'bg-activity-mid' :
                  'bg-activity-high';

  return (
    <Layout>
      <div className="min-h-screen bg-background py-8 md:py-12">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-8 md:mb-12">
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-3 text-foreground">⭐ 学习进度</h1>
              <p className="text-lg md:text-xl text-muted-foreground">看看你的学习成果吧！</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              <div className="space-y-6 md:space-y-8">
                <motion.div
                  className="bg-card border-2 border-border rounded-3xl p-6 md:p-8 shadow-md"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <h2 className="text-2xl md:text-3xl font-heading font-bold mb-6 flex items-center gap-3">
                    <span>📊</span><span>总体统计</span>
                  </h2>
                  <div className="space-y-6">
                    <div className="flex items-center gap-4">
                      <div className="text-5xl">🔥</div>
                      <div className="flex-1">
                        <div className="text-sm text-muted-foreground mb-1">连续学习</div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-bold text-primary">{stats.streak}</span>
                          <span className="text-xl text-muted-foreground">天</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-5xl">📚</div>
                      <div className="flex-1">
                        <div className="text-sm text-muted-foreground mb-1">已学单词</div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-bold text-primary">{stats.totalLearned}</span>
                          <span className="text-xl text-muted-foreground">个</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-5xl">⚡</div>
                      <div className="flex-1">
                        <div className="text-sm text-muted-foreground mb-1">积分</div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-bold text-primary">{storage.points}</span>
                          <span className="text-xl text-muted-foreground">分</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="bg-card border-2 border-border rounded-3xl p-6 md:p-8 shadow-md"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <h2 className="text-2xl md:text-3xl font-heading font-bold mb-6 flex items-center gap-3">
                    <span>🌍</span><span>分语言掌握度</span>
                  </h2>
                  <div className="space-y-6">
                    <div className="bg-lang-zh-bg border-2 border-lang-zh-border rounded-2xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-4xl">🇨🇳</span>
                        <div className="flex-1">
                          <div className="text-lg font-bold">中文</div>
                          <div className="text-3xl font-bold text-lang-zh">{stats.masteryZh}%</div>
                        </div>
                      </div>
                      <ProgressBar value={stats.masteryZh} className="h-3" />
                    </div>

                    <div className="bg-lang-jp-bg border-2 border-lang-jp-border rounded-2xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-4xl">🇯🇵</span>
                        <div className="flex-1">
                          <div className="text-lg font-bold">日文</div>
                          <div className="text-3xl font-bold text-lang-jp">{stats.masteryJp}%</div>
                        </div>
                      </div>
                      <ProgressBar value={stats.masteryJp} className="h-3" />
                    </div>

                    <div className="bg-lang-en-bg border-2 border-lang-en-border rounded-2xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-4xl">🇬🇧</span>
                        <div className="flex-1">
                          <div className="text-lg font-bold">英文</div>
                          <div className="text-3xl font-bold text-lang-en">{stats.masteryEn}%</div>
                        </div>
                      </div>
                      <ProgressBar value={stats.masteryEn} className="h-3" />
                    </div>
                  </div>
                </motion.div>
              </div>

              <div className="space-y-6 md:space-y-8">
                <motion.div
                  className="bg-card border-2 border-border rounded-3xl p-6 md:p-8 shadow-md"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h2 className="text-2xl md:text-3xl font-heading font-bold mb-6 flex items-center gap-3">
                    <span>📅</span><span>30天学习记录</span>
                  </h2>
                  <div className="grid grid-cols-10 gap-1.5 md:gap-2">
                    {activityCalendar.map((day, index) => (
                      <motion.div
                        key={index}
                        className={`aspect-square rounded-lg ${calColor(day.level)} transition-all`}
                        title={`${day.date}: ${day.level > 0 ? '已学习' : '未学习'}`}
                        whileHover={{ scale: 1.2 }}
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-center gap-3 md:gap-4 mt-5 text-xs md:text-sm text-muted-foreground flex-wrap">
                    {[
                      ['bg-muted/30 border border-border', '无活动'],
                      ['bg-activity-low', '少量'],
                      ['bg-activity-mid', '较多'],
                      ['bg-activity-high', '很多']
                    ].map(([cls, lbl]) => (
                      <div key={lbl} className="flex items-center gap-1.5">
                        <div className={`w-3 h-3 md:w-4 md:h-4 rounded ${cls}`} />
                        <span>{lbl}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                <motion.div
                  className="bg-card border-2 border-border rounded-3xl p-6 md:p-8 shadow-md"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <h2 className="text-2xl md:text-3xl font-heading font-bold mb-6 flex items-center gap-3">
                    <span>🏆</span><span>成就徽章</span>
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {achievements.map(achievement => (
                      <motion.div
                        key={achievement.id}
                        className={`relative rounded-2xl p-4 text-center transition-all ${
                          achievement.unlocked
                            ? 'bg-mastery-familiar-bg border-2 border-mastery-familiar-border shadow-md'
                            : 'bg-muted/40 border-2 border-muted grayscale opacity-50'
                        }`}
                        whileHover={achievement.unlocked ? { scale: 1.06, rotate: 2 } : {}}
                        transition={{ type: 'spring', stiffness: 300 }}
                      >
                        {!achievement.unlocked && (
                          <div className="absolute inset-0 flex items-center justify-center bg-background/40 rounded-2xl">
                            <div className="text-2xl">🔒</div>
                          </div>
                        )}
                        <div className="text-4xl mb-2">{achievement.emoji}</div>
                        <div className="text-sm font-bold mb-1">{achievement.title}</div>
                        <div className="text-xs text-muted-foreground">{achievement.description}</div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>

            {categoryProgress.length > 0 && (
              <motion.div
                className="bg-card border-2 border-border rounded-3xl p-6 md:p-8 shadow-md mt-6 md:mt-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <h2 className="text-2xl md:text-3xl font-heading font-bold mb-6 flex items-center gap-3">
                  <span>📂</span><span>分类进度</span>
                </h2>
                <div className="space-y-4">
                  {categoryProgress.map(({ category, total, masteredZh, masteredJp, masteredEn }) => (
                    <div key={category.id} className="bg-muted/30 rounded-2xl p-4 md:p-5">
                      <div className="font-bold text-lg md:text-xl mb-4 flex items-center gap-2">
                        <span className="text-2xl">{category.emoji}</span>
                        <span>{category.label}</span>
                        <span className="text-sm text-muted-foreground ml-auto">共{total}词</span>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <div className="flex items-center justify-between mb-2 text-sm">
                            <span className="flex items-center gap-1.5">
                              <span>🇨🇳</span>
                              <span>中文</span>
                            </span>
                            <span className="font-bold text-lang-zh">
                              {masteredZh}/{total} ({total > 0 ? Math.round((masteredZh / total) * 100) : 0}%)
                            </span>
                          </div>
                          <ProgressBar value={total > 0 ? (masteredZh / total) * 100 : 0} className="h-2" />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-2 text-sm">
                            <span className="flex items-center gap-1.5">
                              <span>🇯🇵</span>
                              <span>日文</span>
                            </span>
                            <span className="font-bold text-lang-jp">
                              {masteredJp}/{total} ({total > 0 ? Math.round((masteredJp / total) * 100) : 0}%)
                            </span>
                          </div>
                          <ProgressBar value={total > 0 ? (masteredJp / total) * 100 : 0} className="h-2" />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-2 text-sm">
                            <span className="flex items-center gap-1.5">
                              <span>🇬🇧</span>
                              <span>英文</span>
                            </span>
                            <span className="font-bold text-lang-en">
                              {masteredEn}/{total} ({total > 0 ? Math.round((masteredEn / total) * 100) : 0}%)
                            </span>
                          </div>
                          <ProgressBar value={total > 0 ? (masteredEn / total) * 100 : 0} className="h-2" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {categoryProgress.length === 0 && (
              <motion.div
                className="bg-card border-2 border-border rounded-3xl p-12 shadow-md mt-6 md:mt-8 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <div className="text-6xl mb-4">📚</div>
                <p className="text-xl text-muted-foreground">还没有学习任何单词</p>
                <p className="text-sm text-muted-foreground mt-2">快去首页开始今日学习吧！</p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}